import { Check, ChevronsUpDown, Plus, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { routes } from '@/app/routes';
import type { Workspace } from '@/shared/api/types';
import { cn } from '@/shared/lib/cn';
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Tooltip } from '@/shared/ui';
import { useWorkspace } from './WorkspaceProvider';

/*
 * A workspace is told apart at a glance by a tinted monogram, the way Slack and Linear do it.
 * The tint comes from the name, so it is stable across sessions without being stored anywhere.
 * These are muted hues on purpose: the accent red stays reserved for the primary action.
 */
const TINTS = [
  'bg-info-subtle text-info',
  'bg-success-subtle text-success',
  'bg-warning-subtle text-warning',
  'bg-[rgb(var(--ring)/0.18)] text-ring',
  'bg-surface-hover text-fg',
];

function tintFor(name: string): string {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return TINTS[hash % TINTS.length]!;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'W';
  const letters = words.length === 1 ? words[0]!.slice(0, 2) : `${words[0]![0]}${words[words.length - 1]![0]}`;
  return letters.toUpperCase();
}

export function WorkspaceMonogram({ workspace, className }: { workspace: Pick<Workspace, 'name'>; className?: string }) {
  return (
    <span
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-2xs font-bold tracking-tight',
        tintFor(workspace.name),
        className,
      )}
      aria-hidden
    >
      {initials(workspace.name)}
    </span>
  );
}

/**
 * The sidebar's workspace switcher. It used to be a native <select>, whose popup is drawn by the
 * operating system — a light, blue-highlighted menu in the middle of a dark app, with nowhere to
 * put a second line or the way to manage workspaces.
 */
export function WorkspaceSwitcher({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  const { workspaces, active, loading, select } = useWorkspace();

  if (loading && !active) {
    return <div className={cn('skeleton rounded-lg', collapsed ? 'mx-auto h-9 w-9' : 'h-11 w-full')} aria-hidden />;
  }

  const name = active?.name ?? t('workspaces.title');
  const trigger = (
    <MenuTrigger
      aria-label={collapsed ? `${t('workspaces.current')}: ${name}` : undefined}
      className={cn(
        'group flex w-full cursor-pointer items-center gap-2.5 rounded-lg text-left outline-none',
        'transition-colors duration-150 hover:bg-surface-hover data-[state=open]:bg-surface-hover',
        collapsed ? 'h-10 justify-center' : 'h-11 border border-border bg-surface px-2 hover:border-border-strong',
      )}
    >
      <WorkspaceMonogram workspace={{ name }} />
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1">
            <span className="block text-2xs font-medium uppercase tracking-wider text-fg-subtle">
              {t('workspaces.current')}
            </span>
            <span className="block truncate text-sm font-semibold text-fg">{name}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-fg-subtle transition-colors group-hover:text-fg" />
        </>
      )}
    </MenuTrigger>
  );

  return (
    <Menu>
      {collapsed ? (
        <Tooltip content={name} side="right">
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      <MenuContent
        align="start"
        side={collapsed ? 'right' : 'bottom'}
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[15rem] max-w-[18rem]"
      >
        <MenuLabel>{t('workspaces.switchTo')}</MenuLabel>
        <div className="max-h-[min(20rem,50vh)] overflow-y-auto">
          {workspaces.map((item) => {
            const current = item.id === active?.id;
            return (
              <MenuItem
                key={item.id}
                onSelect={() => select(item.id)}
                aria-current={current || undefined}
                className="items-center gap-2.5"
              >
                <WorkspaceMonogram workspace={item} className="h-6 w-6" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{item.name}</span>
                  <span className="block truncate text-2xs text-fg-subtle">
                    {t('workspaces.projectCount', { count: item.project_count })}
                    {item.socialforge_connected && ` · SocialForge`}
                  </span>
                </span>
                {current && <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden />}
              </MenuItem>
            );
          })}
        </div>
        <MenuSeparator />
        <MenuItem asChild>
          <Link to={`${routes.workspaces}?create=1`} className="no-underline">
            <Plus className="h-4 w-4 shrink-0 text-fg-subtle" />
            {t('workspaces.create')}
          </Link>
        </MenuItem>
        <MenuItem asChild>
          <Link to={routes.workspaces} className="no-underline">
            <Settings2 className="h-4 w-4 shrink-0 text-fg-subtle" />
            {t('workspaces.manage')}
          </Link>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}
